<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>Group Status</template>
        <template v-slot:subheader
            >Status change for <strong>{{ props.groupName }}</strong></template
        >

        <page-loading v-if="loading" class="py-8" />

        <group-status-radios
            v-else-if="currentStatus"
            v-model="selected"
            :current-status="currentStatus"
            :is-billable="isBillable"
        />

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    :disabled="!canSubmit"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                    data-test="group-status-submit"
                    @click.prevent="onPrimaryClick"
                >
                    Update Now
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { VxButton, VxModal, VxSwal } from '@voxie/frontend-components';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupStatusRadios from '~/components/kiosk/kiosk-groups/GroupStatusRadios.vue';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminGroupsClient, groupsClient } from '~/services';
import { GROUP_STATUS_CLOSED, GROUP_STATUS_DRAFT, type GroupStatus } from '~/constants/groupStatuses';

const props = defineProps<{
    teamId: number;
    groupId: string;
    groupName: string;
}>();

const visible = defineModel<boolean>('visible', { default: false });

const emit = defineEmits<{
    updated: [status: GroupStatus];
}>();

const toasted = useToasted();
const { catchError } = useFormRequestHandler();

const loading = ref(false);
const submitting = ref(false);
const currentStatus = ref<GroupStatus | undefined>(undefined);
const isBillable = ref(false);
const selected = ref<GroupStatus>(GROUP_STATUS_DRAFT);

const canSubmit = computed(
    () => !loading.value && !!currentStatus.value && selected.value !== currentStatus.value && !submitting.value
);

const loadGroup = async () => {
    loading.value = true;
    try {
        const response = await groupsClient.getGroup({ teamId: props.teamId }, props.groupId);
        currentStatus.value = response.data.status;
        isBillable.value = Boolean(response.data.is_billable);
        selected.value = response.data.status;
    } catch (e) {
        catchError(e);
        visible.value = false;
    } finally {
        loading.value = false;
    }
};

watch(
    visible,
    (value) => {
        if (value) {
            currentStatus.value = undefined;
            isBillable.value = false;
            loadGroup();
        }
    },
    { immediate: true }
);

const onPrimaryClick = () => {
    if (!canSubmit.value) {
        return;
    }

    if (selected.value === GROUP_STATUS_CLOSED) {
        VxSwal.confirmation(
            'Close Group',
            'Closing this group will release all assigned phone numbers. Released numbers cannot be recovered by this group. This action cannot be undone.',
            'danger',
            () => submitImmediate()
        );
        return;
    }

    submitImmediate();
};

const submitImmediate = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.updateStatus(props.teamId, props.groupId, {
            status: selected.value,
        });
        emit('updated', selected.value);
        toasted.success('Group status updated successfully');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
